import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, ButtonBase, Chip, Divider, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveToolCallMetadata } from './toolCallMetadata';
import {
  ToolCallData,
  ToolCallLifecycleStatus,
} from '@shared/services/conversationApiService/types';
import { ToolCallArguments } from './ToolCallArguments';
import { useStyles } from './styles';
import {
  formatToolCallValue,
  getToolCallDuration,
  getToolCallStatusLabel,
} from './toolCallCard.utils';

interface ToolCallCardProps {
  toolCall: ToolCallData;
}

const titleKeys: Record<ToolCallLifecycleStatus, string> = {
  done: 'campaignPage.toolCall.executed',
  error: 'campaignPage.toolCall.failed',
  cancelled: 'campaignPage.toolCall.cancelled',
};

export const ToolCallCard: React.FC<ToolCallCardProps> = ({ toolCall }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const metadata = resolveToolCallMetadata(toolCall.function_call.name, toolCall.source);
  const isExternal = toolCall.source !== 'local';
  const ToolIcon = isExternal ? ExtensionOutlinedIcon : SettingsOutlinedIcon;
  const isCompleted = toolCall.lifecycleStatus === 'done';
  const { classes, cx } = useStyles();
  const detailsId = `tool-call-details-${toolCall.function_call.call_id}`;

  return (
    <Box className={classes.card}>
      <Stack direction="row" alignItems="center" spacing={1.5} className={classes.header}>
        <Box className={cx(classes.icon, isExternal && classes.externalIcon)}>
          <ToolIcon />
        </Box>
        <Box className={classes.titleContainer}>
          <Typography variant="body1" className={classes.title}>
            {t(titleKeys[toolCall.lifecycleStatus])}
          </Typography>
          <Typography className={classes.toolName}>{toolCall.function_call.name}</Typography>
        </Box>
        <Chip
          label={getToolCallStatusLabel(toolCall.lifecycleStatus, t)}
          size="small"
          className={cx(
            classes.status,
            isCompleted && classes.completedStatus,
            toolCall.lifecycleStatus === 'cancelled' && classes.cancelledStatus,
          )}
        />
      </Stack>

      <Divider className={classes.divider} />

      <ButtonBase
        component="button"
        type="button"
        aria-controls={detailsId}
        aria-expanded={isExpanded}
        onClick={(event) => {
          event.stopPropagation();
          setIsExpanded((expanded) => !expanded);
        }}
        className={classes.detailsButton}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1.5}
          className={classes.detailsButtonContent}>
          <Typography className={classes.detailsButtonLabel}>
            {t(
              isExpanded
                ? 'campaignPage.toolCall.hideDetails'
                : 'campaignPage.toolCall.showDetails',
            )}
          </Typography>
          {isExpanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
        </Stack>
      </ButtonBase>

      {isExpanded && (
        <Box id={detailsId} className={classes.details}>
          <Stack spacing={1}>
            <Box>
              <Typography variant="caption" className={classes.fieldLabel}>
                {t('campaignPage.toolCall.tool')}
              </Typography>
              <Typography className={classes.fieldValue}>{toolCall.function_call.name}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" className={classes.fieldLabel}>
                {t('campaignPage.toolCall.arguments')}
              </Typography>
              <ToolCallArguments rawValue={toolCall.function_call.arguments} />
            </Box>
            {toolCall.function_call_output && (
              <Box>
                <Typography variant="caption" className={classes.fieldLabel}>
                  {t('campaignPage.toolCall.result')}
                </Typography>
                <Typography
                  component="pre"
                  variant="body2"
                  className={cx(
                    classes.output,
                    toolCall.function_call_output.is_error && classes.errorOutput,
                  )}>
                  {formatToolCallValue(toolCall.function_call_output.output)}
                </Typography>
              </Box>
            )}
            <Typography variant="caption" className={classes.description}>
              {t(metadata.descriptionKey)} ·{' '}
              {t('campaignPage.toolCall.duration', { duration: getToolCallDuration(toolCall) })}
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};
